import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { colors, radius, shadows } from '@/theme/tokens';

export interface SubscriptionSnapshot {
  paymentProvider?: string;
  stripeSubscriptionId?: string | null;
  subscriptionStatus?: string;
  cancelAtPeriodEnd?: boolean;
  /** 期日指定の終了（2次直前パスはここに 2026-10-26 が入る）。無ければ null */
  cancelAt?: Date | null;
  premiumUntil?: Date | null;
  priceNickname?: string | null;
  priceAmount?: number | null;
  priceCurrency?: string | null;
  cardLast4?: string | null;
  isTestMode?: boolean;
}

interface Props {
  snapshot: SubscriptionSnapshot;
  productName: string;
  onManage: () => Promise<void>;
  manageDisabled?: boolean;
  manageHint?: string;
  onSync?: () => Promise<void>;
}

const dateFormatter = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

const statusMeta: Record<
  string,
  { label: string; color: string; backgroundColor: string }
> = {
  active: {
    label: '有効',
    color: colors.success,
    backgroundColor: alpha(colors.success, 0.14),
  },
  trialing: {
    label: 'トライアル中',
    color: colors.info,
    backgroundColor: alpha(colors.info, 0.14),
  },
  past_due: {
    label: '支払い未確認',
    color: colors.error,
    backgroundColor: alpha(colors.error, 0.14),
  },
  canceled: {
    label: '解約済み',
    color: colors.textSecondary,
    backgroundColor: alpha(colors.textSecondary, 0.12),
  },
  cancelled: {
    label: '解約済み',
    color: colors.textSecondary,
    backgroundColor: alpha(colors.textSecondary, 0.12),
  },
};

const normalizeStatus = (status?: string) => status?.trim().toLowerCase() ?? '';

const formatCurrency = (
  amount?: number | null,
  currency?: string | null,
): string | null => {
  if (amount == null || !currency) {
    return null;
  }

  try {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: currency.toLowerCase() === 'jpy' ? 0 : undefined,
    }).format(amount);
  } catch {
    return null;
  }
};

export const formatUntil = (snapshot: SubscriptionSnapshot): string => {
  const until = snapshot.premiumUntil;

  if (!until) {
    return '更新日を取得しています…';
  }

  const formattedDate = dateFormatter.format(until);

  // **終わる日が決まっているなら「更新されます」と書かない。**
  // 2次直前パスは cancel_at に 10/26 が立っている（cancel_at_period_end は false）。
  // ここで cancelAt を見ないと「10月26日に ¥980 が更新されます」と出る＝起きないことを書く。
  if (snapshot.cancelAtPeriodEnd || snapshot.cancelAt) {
    return `${formattedDate}まで利用できます（更新なし）`;
  }

  const formattedAmount = formatCurrency(snapshot.priceAmount, snapshot.priceCurrency);

  if (!formattedAmount) {
    return `${formattedDate}に更新されます`;
  }

  return `${formattedDate}に ${formattedAmount} が更新されます`;
};

export const remainingDays = (until: Date, now: Date = new Date()): number =>
  Math.ceil((until.getTime() - now.getTime()) / 86400000);

export default function SubscriptionStatusCard({
  snapshot,
  productName,
  onManage,
  manageDisabled = false,
  manageHint,
  onSync,
}: Props) {
  const [isManaging, setIsManaging] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);
  const hasSyncedRef = useRef(false);

  const status = normalizeStatus(snapshot.subscriptionStatus);
  const currentStatus = statusMeta[status] ?? {
    label: snapshot.subscriptionStatus || '状態不明',
    color: colors.textSecondary,
    backgroundColor: alpha(colors.textSecondary, 0.12),
  };
  const isPastDue = status === 'past_due';
  const planName = snapshot.priceNickname || productName;
  const daysRemaining = useMemo(
    () => (snapshot.premiumUntil ? remainingDays(snapshot.premiumUntil) : null),
    [snapshot.premiumUntil],
  );
  const refundHref = `mailto:contact@tech-athletes.com?subject=${encodeURIComponent(
    `返金相談 ${productName} ${snapshot.stripeSubscriptionId || ''}`,
  )}`;

  useEffect(() => {
    if (snapshot.premiumUntil || !onSync || hasSyncedRef.current) {
      return;
    }

    hasSyncedRef.current = true;
    void onSync();
  }, [onSync, snapshot.premiumUntil]);

  const handleManage = async () => {
    setIsManaging(true);
    setManageError(null);

    try {
      await onManage();
    } catch (error) {
      setManageError(error instanceof Error ? error.message : '処理に失敗しました');
    } finally {
      setIsManaging(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: `${radius.lg}px`,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.surface,
        boxShadow: shadows.card,
        color: colors.textPrimary,
      }}
    >
      {snapshot.isTestMode && (
        <Chip
          label="テスト"
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            height: 22,
            bgcolor: colors.surfaceHigh,
            color: colors.textSecondary,
            border: `1px solid ${colors.border}`,
            fontSize: '0.72rem',
          }}
        />
      )}

      {isPastDue && (
        <Box
          sx={{
            borderBottom: `1px solid ${alpha(colors.error, 0.36)}`,
            bgcolor: alpha(colors.error, 0.12),
            px: { xs: 2, sm: 3 },
            py: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2" sx={{ color: colors.textPrimary, pr: { sm: 2 } }}>
              支払いが確認できません。支払い方法を更新してください
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleManage}
              disabled={isManaging || manageDisabled}
              sx={{
                flexShrink: 0,
                borderColor: colors.error,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: colors.error,
                  bgcolor: alpha(colors.error, 0.1),
                },
              }}
            >
              支払い方法を更新
            </Button>
          </Stack>
        </Box>
      )}

      <Box sx={{ p: { xs: 2, sm: 3 }, pt: snapshot.isTestMode ? { xs: 5, sm: 3 } : undefined }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            sx={{ pr: snapshot.isTestMode ? { sm: 8 } : undefined }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" sx={{ color: colors.textSecondary }}>
                現在のプラン
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                {planName}
              </Typography>
            </Box>
            <Chip
              label={currentStatus.label}
              sx={{
                flexShrink: 0,
                bgcolor: currentStatus.backgroundColor,
                color: currentStatus.color,
                border: `1px solid ${alpha(currentStatus.color, 0.34)}`,
                fontWeight: 700,
              }}
            />
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            <Box
              sx={{
                flex: '1 1 220px',
                minWidth: 0,
                borderRadius: `${radius.lg}px`,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.surfaceHigh,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.75 }}>
                次回更新日 / 利用期限
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                {formatUntil(snapshot)}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: '0 1 150px',
                minWidth: { xs: '100%', sm: 150 },
                borderRadius: `${radius.lg}px`,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.goldSoft,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                残り日数
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: colors.gold,
                  fontWeight: 800,
                  lineHeight: 1.1,
                  fontFeatureSettings: '"tnum" 1',
                }}
              >
                {daysRemaining == null
                  ? '確認中'
                  : daysRemaining > 0
                    ? `あと ${daysRemaining} 日`
                    : '本日まで'}
              </Typography>
            </Box>
          </Box>

          {snapshot.cardLast4 && (
            <Typography variant="body2" sx={{ color: colors.textSecondary }}>
              カード •••• {snapshot.cardLast4}
            </Typography>
          )}

          <Stack spacing={1.25}>
            {manageDisabled ? (
              manageHint && (
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  {manageHint}
                </Typography>
              )
            ) : (
              <Box>
                <Button
                  variant="contained"
                  onClick={handleManage}
                  disabled={isManaging}
                  sx={{
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' },
                    bgcolor: colors.gold,
                    color: '#0E1116',
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: colors.gold,
                    },
                    '&.Mui-disabled': {
                      bgcolor: alpha(colors.gold, 0.5),
                      color: alpha('#0E1116', 0.7),
                    },
                  }}
                >
                  {isManaging ? (
                    <CircularProgress size={20} sx={{ color: '#0E1116' }} />
                  ) : (
                    '購読を管理'
                  )}
                </Button>
                {manageError && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: colors.error }}>
                    {manageError}
                  </Typography>
                )}
              </Box>
            )}

            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <Link href={refundHref} underline="hover" sx={{ color: colors.textSecondary }}>
                返金の相談
              </Link>
              <Link
                href="https://tech-athletes.com/terms/"
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ color: colors.textSecondary }}
              >
                利用規約
              </Link>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
